import { useCallback, useEffect, useState } from 'react';
import { fetchActiveProductPreview } from '../../salon-admin/utils/checkinQueueApi';

const POLL_MS = 3000;

function cartFingerprint(cart) {
  if (!Array.isArray(cart) || !cart.length) return '';
  return cart
    .map((item) => {
      const product = item?.product && typeof item.product === 'object' ? item.product : item;
      const id = product?.id ?? product?.product_id ?? product?.product_name ?? product?.name ?? '';
      const qty = Number(item?.quantity || product?.quantity || 0);
      return `${id}:${qty}`;
    })
    .sort()
    .join('|');
}

function previewSignature(data) {
  if (!data?.active) return 'off';
  const products = data.recommended_products || {};
  const morning = Array.isArray(products.morning) ? products.morning.length : 0;
  const evening = Array.isArray(products.evening) ? products.evening.length : 0;
  const purchases = Array.isArray(data.product_purchases) ? data.product_purchases.length : 0;
  return [
    data.checkin_id,
    morning,
    evening,
    purchases,
    cartFingerprint(data.product_cart),
    data.customer_name || '',
  ].join('::');
}

/**
 * When front desk opens product upsell, Passport shows the same products.
 * Clears automatically when desk closes preview (returns to idle video).
 */
export function usePassportProductPreview(active) {
  const [preview, setPreview] = useState(null);

  const refresh = useCallback(async () => {
    if (!active) {
      setPreview(null);
      return;
    }
    try {
      const data = await fetchActiveProductPreview();
      if (!data?.active) {
        setPreview(null);
        return;
      }
      setPreview((prev) => {
        if (prev && previewSignature(prev) === previewSignature(data)) return prev;
        return data;
      });
    } catch {
      /* keep last preview — avoids flashing idle video over product upsell */
    }
  }, [active]);

  const applyCart = useCallback((cart) => {
    setPreview((prev) => {
      if (!prev?.active) return prev;
      return { ...prev, product_cart: Array.isArray(cart) ? cart : [] };
    });
  }, []);

  useEffect(() => {
    if (!active) {
      setPreview(null);
      return undefined;
    }
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      await refresh();
    };
    void tick();
    const timer = setInterval(tick, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [active, refresh]);

  return { preview, applyCart, refresh };
}
